/*
 * <dev-card-media> — hover image cycler for dev-product-card.
 *
 * Wraps the card's media link. While the pointer is over the card (devices
 * that actually support hover), it cycles through the stacked product images
 * on a timer, crossfading between them via the `.is-active` class. On leave it
 * resets to the primary image.
 *
 * Progressive enhancement:
 *  - No JS / element not upgraded → CSS keeps the primary→secondary hover
 *    swap (see `dev-product-card.css`, gated on `:not(.is-ready)`).
 *  - Touch / no-hover devices → primary image only, no cycling.
 *  - `prefers-reduced-motion: reduce` → primary image only, no cycling.
 *
 * Cards added after load (cart drawer, AJAX grids) self-init via
 * connectedCallback, so no re-scan is needed.
 */
if (!customElements.get('dev-card-media')) {
  class DevCardMedia extends HTMLElement {
    connectedCallback() {
      this.images = Array.from(this.querySelectorAll('.dev-product-card__image'));

      // Mark as enhanced so the CSS-only hover fallback stands down. Done even
      // when we won't cycle (single image / touch / reduced motion) so the
      // fallback never fights the JS-controlled `.is-active` state.
      this.classList.add('is-ready');

      if (this.images.length < 2) return;

      const canHover = window.matchMedia('(hover: hover)').matches;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!canHover || reduceMotion) return;

      this.index = 0;
      this.intervalMs = parseInt(this.dataset.interval, 10) || 700;
      this.timer = null;

      this._onEnter = this.start.bind(this);
      this._onLeave = this.stop.bind(this);
      this.addEventListener('pointerenter', this._onEnter);
      this.addEventListener('pointerleave', this._onLeave);
    }

    disconnectedCallback() {
      this.stop();
      this.removeEventListener('pointerenter', this._onEnter);
      this.removeEventListener('pointerleave', this._onLeave);
    }

    show(next) {
      if (next === this.index) return;
      this.images[this.index].classList.remove('is-active');
      this.images[next].classList.add('is-active');
      this.index = next;
    }

    start() {
      this.stop();
      this.timer = setInterval(() => {
        this.show((this.index + 1) % this.images.length);
      }, this.intervalMs);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      this.show(0);
    }
  }

  customElements.define('dev-card-media', DevCardMedia);
}
